/* a-garaia-shop-infinite.js
 * Infinite scroll for the shop collection grid.
 * - IntersectionObserver on the load-sentinel triggers a fetch
 *   of the next page via the Shopify Section Rendering API.
 * - sessionStorage keyed by URL preserves loaded-page count and
 *   scroll position so a back-nav from a PDP lands the shopper
 *   exactly where they left off.
 */

(function () {
  'use strict';

  function init() {
    var grid = document.querySelector('[data-shop-grid]');
    if (!grid) return;

    var sentinel = document.querySelector('[data-shop-load-more]');
    var sectionId = grid.getAttribute('data-section-id');
    if (!sectionId) return;

    var stateKey = 'shop-infinite:' + location.pathname + location.search;
    var loadedPages = 1;
    var fetchInFlight = false;

    function persistState() {
      try {
        sessionStorage.setItem(stateKey, JSON.stringify({
          loadedPages: loadedPages,
          scrollY: window.scrollY
        }));
      } catch (e) { /* sessionStorage may be unavailable; ignore */ }
    }

    function readState() {
      try {
        var raw = sessionStorage.getItem(stateKey);
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    }

    /* Fetch the page at nextUrl via Section Rendering API and
     * append its product cards to the grid. Updates the sentinel's
     * data-next-url to the next page (parsed from the response).
     * If response has no next sentinel, removes our sentinel. */
    function loadNext(nextUrl) {
      if (fetchInFlight || !nextUrl) return Promise.resolve(false);
      fetchInFlight = true;

      var separator = nextUrl.indexOf('?') >= 0 ? '&' : '?';
      var fetchUrl = nextUrl + separator + 'section_id=' + encodeURIComponent(sectionId);

      return fetch(fetchUrl, { credentials: 'same-origin' })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('[data-shop-grid]');
          if (!newGrid) {
            fetchInFlight = false;
            return false;
          }

          /* Append each product card from the response to the live grid.
           * Skip placeholder cards (the empty-state visual). */
          var cards = newGrid.querySelectorAll('.shop-page__card:not(.shop-page__card--placeholder)');
          cards.forEach(function (card) {
            grid.appendChild(card);
          });

          /* Update or remove the sentinel based on the response's own sentinel. */
          var newSentinel = doc.querySelector('[data-shop-load-more]');
          if (newSentinel && sentinel) {
            sentinel.setAttribute('data-next-url', newSentinel.getAttribute('data-next-url'));
          } else if (sentinel) {
            sentinel.remove();
            sentinel = null;
          }

          loadedPages += 1;
          persistState();
          fetchInFlight = false;
          return true;
        })
        .catch(function () { fetchInFlight = false; return false; });
    }

    /* Restore state on back-nav (or reload after we'd loaded multiple pages).
     * Sequentially fetch pages 2..targetPages, then scroll to saved Y. */
    function restoreFromState() {
      var state = readState();
      if (!state || state.loadedPages <= 1) return Promise.resolve();

      var target = state.loadedPages;
      var chain = Promise.resolve();
      var _i;
      for (_i = 1; _i < target; _i++) {
        chain = chain.then(function () {
          if (!sentinel) return false;
          return loadNext(sentinel.getAttribute('data-next-url'));
        });
      }
      return chain.then(function () {
        if (typeof state.scrollY === 'number') {
          window.scrollTo(0, state.scrollY);
        }
      });
    }

    /* Persist scroll Y while shopper scrolls (debounced). */
    var scrollTimer = null;
    window.addEventListener('scroll', function () {
      if (scrollTimer) return;
      scrollTimer = setTimeout(function () {
        persistState();
        scrollTimer = null;
      }, 200);
    }, { passive: true });

    /* Persist on hide (covers tab close, refresh, navigation). */
    window.addEventListener('beforeunload', persistState);

    function startObserving() {
      /* IntersectionObserver: when sentinel approaches viewport, fetch next page.
         Registered AFTER restoreFromState resolves so observer fires don't race
         with the restore chain (which would cause loadedPages to be off by one). */
      if (sentinel && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && sentinel) {
              loadNext(sentinel.getAttribute('data-next-url'));
            }
          });
        }, { rootMargin: '400px 0px' });
        observer.observe(sentinel);
      }
    }

    /* Restore on initial load, then start observing.
       On bfcache restore (back-nav with full DOM cached), the appended cards
       are still in place and we only need to re-scroll. */
    restoreFromState().then(startObserving);
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        /* bfcache restore: state already in place; just rescroll. */
        var state = readState();
        if (state && typeof state.scrollY === 'number') {
          window.scrollTo(0, state.scrollY);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
